import React, { useState, useEffect, useRef, useMemo } from 'react';
import { db } from '../firebase';
import { collection, getDoc, onSnapshot, serverTimestamp, doc, updateDoc, setDoc } from 'firebase/firestore';
import { useAuth } from '../AuthProvider';
import { useUserPrefs } from '../UserPrefsProvider';
import PageWrapperAuto from '../components/PageWrapperAuto.js';
import { useUI } from '../UIContext';
import Login from '../Login';
import { useNavigate } from 'react-router-dom';
import { useActiveSession } from '../ActiveSessionContext';
import AbcNotation from '../components/AbcNotation';
import AbcImportPreview from '../components/AbcImportPreview';
import KeyInput from '../components/KeyInput'; // adjust path as needed
import { normalizeKey, normalizeType, keyRoot, normalizeTagsArray, normalizeTag, getNormalizedGenreOptions } from '../utils/normalize';
import { sanitizeAbcBody } from '../components/sanitizeAbcBody';
import myIcon from '../assets/logo512.png'; // adjust path
import { cleanAndFillAbc } from '../utils/cleanAndFillAbc';


import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { storage } from "../firebase"; // make sure this is correct
import CustomTagInput from '../components/CustomTagInput';
import dexie from '../offlineDB';

import { addPendingWrite, flushPendingWrites } from '../utils/flushPendingWrites'; // you already have this
import TuneRecorder from '../components/TuneRecorder'; // Adjust path if needed

import { setDirtyFlag } from '../safeNavigate';
import { useRecording } from '../RecordingContext';
import { getModeLabels, getModeLabelsForEntity, resolveModeForEntity } from '../utils/modeLabels';
import { useOnboarding } from '../onboarding';

import SessionPromptModal from '../components/SessionPromptModal'; // adjust path as needed
import NotesEditor from '../components/NotesEditor';
import ActionConfirmModal from '../components/ActionConfirmModal';
import AudioPlayerWithSpeed from '../components/AudioPlayerWithSpeed';
import { FaQuestionCircle } from 'react-icons/fa';
import AppSelect from '../components/AppSelect';
import FullscreenNotesEditorOverlay from '../components/FullscreenNotesEditorOverlay';
import { ATTACHMENT_ACCEPT_ATTR, validateAttachmentFile } from '../utils/attachments';
import BulkTuneImporter from '../components/BulkTuneImporter';
import TheSessionImportModal from '../components/TheSessionImportModal';
import useInternalLinkTargets from '../hooks/useInternalLinkTargets';
import { toInternalLinkTargetOptions } from '../utils/internalNoteLinks';

const stripHtmlToText = (value = '') => {
  const raw = String(value || '');
  if (!raw) return '';
  if (!raw.includes('<')) return raw;
  if (typeof window !== 'undefined' && typeof window.DOMParser !== 'undefined') {
    try {
      const doc = new window.DOMParser().parseFromString(raw, 'text/html');
      return (doc.body?.textContent || '').replace(/\u00a0/g, ' ');
    } catch (err) {
      console.warn('Failed to parse notes HTML for text extraction:', err);
    }
  }
  return raw.replace(/<[^>]*>/g, ' ');
};

const firstNonEmptyLine = (value = '') =>
  stripHtmlToText(value)
    .split(/\r?\n/)
    .map((line) => line.trim())
    .find(Boolean) || '';

const formatUntitledDate = () => new Date().toLocaleDateString(undefined, { dateStyle: 'medium' });

export default function AddTunePage() {
  const [pendingSessionTuneId, setPendingSessionTuneId] = useState(null);
const [showSessionPrompt, setShowSessionPrompt] = useState(false);

const [recordings, setRecordings] = useState([]);
const [recordingToDelete, setRecordingToDelete] = useState(null);
const [confirmDiscardRecordingOpen, setConfirmDiscardRecordingOpen] = useState(false);
const [duplicateTuneToConfirm, setDuplicateTuneToConfirm] = useState(null);
const [hasUnsavedRecording, setHasUnsavedRecording] = useState(false);
const [isDirty, setIsDirty] = useState(false);
const openTunepalSearch = () => {
  window.open('https://tunepal.org/', '_blank');
};

const [pastedSessionId, setPastedSessionId] = useState('');



const handleImportFromPastedSessionId = async () => {
  const input = pastedSessionId.trim();
  const match = input.match(/(\d+)/);
  if (!match) {
    alert("Couldn't find a tune ID in that input.");
    return;
  }

  const tuneId = match[1];
  await importFromSession(tuneId); // ✅ shows import preview
};




const openSessionSearchPage = () => {
  const title = newTune?.title?.trim();
  const url = title
    ? `https://thesession.org/search?type=tune&q=${encodeURIComponent(title)}`
    : 'https://thesession.org/tunes';
  window.open(url, '_blank');
};

  const { activeSession: session } = useActiveSession();
  const { inputStyle, textStyle } = useUI();
  const { user } = useAuth();
  const [attachments, setAttachments] = useState([]);

 
const { isRecording, cancelRecording } = useRecording();

  const { refreshCounts, NudgeBanner, isSimpleMode } = useOnboarding();

  const userPrefsContext = useUserPrefs();
  const prefs = userPrefsContext?.prefs;
  const labels = userPrefsContext?.labels;
  const [addingMode, setAddingMode] = useState('tunes');
  const [pendingLockedMode, setPendingLockedMode] = useState(null);
  const [showMoreFields, setShowMoreFields] = useState(false);
  const [showSearchTools, setShowSearchTools] = useState(false);
  const [showTSImportModal, setShowTSImportModal] = useState(false);
  const currentMode = prefs?.appMode || 'tunes';
  const isFullMode = Boolean(prefs?.onboardingComplete && !isSimpleMode);
  const effectiveMode = isFullMode ? addingMode : resolveModeForEntity(currentMode, addingMode);
  const effectiveLabels = isFullMode ? getModeLabels(addingMode) : getModeLabelsForEntity(currentMode, addingMode);
  const singularItemLabel = effectiveMode === 'notes' ? 'Note' : 'Tune';
  const singularItemLabelLower = singularItemLabel.toLowerCase();
  const addFields = prefs?.addTuneFieldsByMode?.[effectiveMode] ?? prefs?.addTuneFields ?? {};
  const showKeyField = addFields?.key !== false && !(effectiveLabels?.autoHideFields || []).includes('key');
  const showComposerField = addFields?.composer !== false && !(effectiveLabels?.autoHideFields || []).includes('composer');
  const isTuneOrAllMode = effectiveMode === 'tunes' || effectiveMode === 'all';
  const showExpandedFields = showMoreFields;
  const getDefaultForMode = (mode, field) => {
    const byModeKey = field === 'genre' ? 'defaultGenreByMode' : 'defaultTypeByMode';
    const legacyKey = field === 'genre' ? 'defaultGenre' : 'defaultType';
    const fallback = field === 'genre'
      ? getModeLabels(mode).defaultGenre
      : getModeLabels(mode).defaultType;
    const resolved = prefs?.[byModeKey]?.[mode] ?? prefs?.[legacyKey] ?? fallback;
    if (field === 'genre' && mode === 'notes' && String(resolved || '').trim().toLowerCase() === 'notes') {
      return 'General';
    }
    return resolved;
  };

  const navigate = useNavigate();

  const [tunes, setTunes] = useState([]);
  const [keySuggestions, setKeySuggestions] = useState([]);

  const [sessionUrl, setSessionUrl] = useState('');
  const [selectedTune, setSelectedTune] = useState(null);

  const [duplicateTune, setDuplicateTune] = useState(null);
  const [confirmationMessage, setConfirmationMessage] = useState(null);
  const [similarTunes, setSimilarTunes] = useState([]);
  const [suggestedTunes, setSuggestedTunes] = useState([]);
  const [dismissSuggestedTunes, setDismissSuggestedTunes] = useState(false);
  const [sessionSearchAbort, setSessionSearchAbort] = useState(null);
  const [customAbcOverride, setCustomAbcOverride] = useState('');
const [newTune, setNewTune] = useState({
  title: '', genre: '', type: '', key: '', status: '', composer: '', rating: '', lyrics: '', quickNote: '', notes: '', link1: '', imageUrl: '', abc: '', recordings: [],
 customTags: [],
 eventDate: ''
});
const [focused, setFocused] = useState(false);
const [fullscreenNotes, setFullscreenNotes] = useState(false);

  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const recordingBlobUrlMapRef = useRef(new Map());
  const [importedTune, setImportedTune] = useState(null);
  const [sessionSearchAttempted, setSessionSearchAttempted] = useState(false);
  const [alertModal, setAlertModal] = useState(null); // { title, message }
const [currentRoot, canonicalDefaults, rawSuggestions] = useMemo(() => {
  const inputKey = (newTune.key || '').trim();
  const root = keyRoot(inputKey);  // 👈 no toUpperCase yet



  

 

  const canon = [
    `${root} major`,
    `${root} minor`,
    `${root} dorian`,
    `${root} mixolydian`
  ];

  const dynamicSet = new Set(canon);
  if (keySuggestions[root]) {
    keySuggestions[root].forEach(k => dynamicSet.add(k));
  }


  return [root, canon, Array.from(dynamicSet)];
}, [newTune.key, keySuggestions]);



const handleDiscardRecording = () => {
  if (!isRecording) return;
  setConfirmDiscardRecordingOpen(true);
};

const getStableRecordingAudioUrl = (rec) => {
  if (rec?.url) return rec.url;
  if (!rec?.localBlob) return null;
  const key = rec.id || `${rec.createdAt || ''}:${rec.label || ''}`;
  const existing = recordingBlobUrlMapRef.current.get(key);
  if (existing) return existing;
  const nextUrl = URL.createObjectURL(rec.localBlob);
  recordingBlobUrlMapRef.current.set(key, nextUrl);
  return nextUrl;
};

  const [tagSuggestions, setTagSuggestions] = useState([]);
  const { notes: linkableNotes, folders: linkableFolders } = useInternalLinkTargets(user, true);
  const internalLinkTargets = useMemo(
    () => toInternalLinkTargetOptions({ notes: linkableNotes, folders: linkableFolders }),
    [linkableNotes, linkableFolders]
  );
  const handleNavigateInternalLink = ({ targetType }) => {
    setAlertModal({
      title: 'Save Before Opening Link',
      message: `This ${targetType === 'folder' ? 'folder' : 'note'} link will open after you save this new note.`,
    });
  };


  const [importFields, setImportFields] = useState({
    title: true, type: true, key: true, abc: true
  });


  
  const handleFileUpload = async (e) => {
  const files = Array.from(e.target.files);

  if (!files.length || !user || !user.uid) {
    alert("Upload blocked: user not fully authenticated.");
    return;
  }

  const uploaded = [];
  const rejected = [];

  for (const file of files) {
    const validation = validateAttachmentFile(file);
    if (!validation.ok) {
      rejected.push(validation.reason);
      continue;
    }
    console.log("📤 Starting upload:", file.name);
    try {
      const storageRef = ref(storage, `recordings/${user.uid}/attachments/temp/${file.name}`);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);

      uploaded.push({
        name: file.name,
        type: file.type,
        url,
        uploadedAt: new Date().toISOString(),
        file
      });
    } catch (err) {
      console.error("❌ Upload failed for", file.name, err?.code, err?.message, err);
      alert(`File upload failed for "${file.name}": ${err?.code || err?.message || 'Unknown error'}`);
    }
  }

  if (rejected.length > 0) {
    alert(rejected.join('\n'));
  }

  setAttachments((prev) => [...prev, ...uploaded]);

  // ✅ Clear the file input box
  if (fileInputRef.current) {
    fileInputRef.current.value = null;
  }
};
// In full mode, keep addingMode in sync with prefs.appMode so the tab reflects the global mode
useEffect(() => {
  if (isFullMode && (currentMode === 'tunes' || currentMode === 'notes')) {
    setAddingMode(currentMode);
  }
}, [currentMode, isFullMode]);

useEffect(() => {
  if (!user) return;

  const preload = async () => {
    try {
      if (navigator.onLine) {
        await flushPendingWrites(user); // 🧠 sync offline writes first
      }
    } catch (err) {
      console.warn("Error syncing pending writes:", err);
    }
  };
  preload();

  const tunesRef = collection(db, 'users', user.uid, 'tunes');
  return onSnapshot(tunesRef, snapshot => {
    const fetchedTunes = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    setTunes(fetchedTunes);

    const tagSet = new Set();
    fetchedTunes.forEach(t => {
      (t.customTags || []).forEach(tag => tagSet.add(tag));
    });
    const tagSuggestions = Array.from(tagSet);
    setTagSuggestions(tagSuggestions);

    const normalizedKeys = fetchedTunes.map(t => normalizeKey(t.key));
    const groupedByRoot = {};
    const roots = ['C', 'C#', 'Db', 'D', 'D#', 'Eb', 'E', 'E#', 'Fb', 'F', 'F#', 'Gb', 'G', 'G#', 'Ab', 'A', 'A#', 'Bb', 'B', 'Cb'];
    roots.forEach(root => {
      groupedByRoot[root.toUpperCase()] = new Set([
        `${root} major`,
        `${root} minor`,
        `${root} dorian`,
        `${root} mixolydian`
      ]);
    });
    normalizedKeys.forEach(nk => {
      const root = keyRoot(nk).toUpperCase();
      if (!groupedByRoot[root]) groupedByRoot[root] = new Set();
      groupedByRoot[root].add(`${root} major`);
      groupedByRoot[root].add(`${root} minor`);
      groupedByRoot[root].add(`${root} dorian`);
      groupedByRoot[root].add(`${root} mixolydian`);
      groupedByRoot[root].add(nk);
    });

    setKeySuggestions(groupedByRoot);
  });
}, [user]);

useEffect(() => {
  const activeKeys = new Set(
    (recordings || []).map((r) => r.id || `${r.createdAt || ''}:${r.label || ''}`)
  );
  for (const [key, url] of recordingBlobUrlMapRef.current.entries()) {
    if (!activeKeys.has(key)) {
      URL.revokeObjectURL(url);
      recordingBlobUrlMapRef.current.delete(key);
    }
  }
}, [recordings]);

useEffect(() => () => {
  for (const [, url] of recordingBlobUrlMapRef.current.entries()) {
    URL.revokeObjectURL(url);
  }
  recordingBlobUrlMapRef.current.clear();
}, []);


// Compute isDirty from actual form content (not sticky)
useEffect(() => {
  const notesText = stripHtmlToText(newTune.notes || '').trim();
  const hasContent = Boolean(
    (newTune.title || '').trim() ||
    notesText ||
    (newTune.lyrics || '').trim() ||
    (newTune.quickNote || '').trim() ||
    (newTune.link1 || '').trim() ||
    (newTune.imageUrl || '').trim() ||
    (newTune.abc || '').trim() ||
    (recordings || []).length > 0 ||
    (attachments || []).length > 0
  );
  setIsDirty(hasContent);
}, [newTune, recordings, attachments]);

useEffect(() => {
  setDirtyFlag(isDirty || hasUnsavedRecording);
}, [isDirty, hasUnsavedRecording]);

useEffect(() => {
  return () => setDirtyFlag(false);
}, []);


  useEffect(() => {
    if (!prefs) return;
    const fallbackStatus = (effectiveLabels?.statusOptions || [])[1] || '';
    const defaultGenre = getDefaultForMode(effectiveMode, 'genre');
    const defaultType = getDefaultForMode(effectiveMode, 'type');
    setNewTune(prev => ({
      ...prev,
      genre: defaultGenre,
      type: defaultType,
      key: effectiveMode === 'notes' ? '' : (prefs.defaultKey || ''),
      status: prefs.defaultStatus === '—' ? '' : (prefs.defaultStatus || fallbackStatus),
      eventDate: prev.eventDate || ''
    }));
  }, [prefs, effectiveMode, effectiveLabels]);

  useEffect(() => {
  if (textareaRef.current) {
    textareaRef.current.style.height = 'auto'; // Reset height
    textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px'; // Set to scroll height
  }
}, [customAbcOverride]);

// Sync key field changes into ABC textarea's K: line
const prevKeyRef = useRef(newTune.key);
useEffect(() => {
  if (!newTune.abc || prevKeyRef.current === newTune.key) return;
  prevKeyRef.current = newTune.key;
  setNewTune(prev => {
    if (!prev.abc) return prev;
    const updated = prev.abc.replace(/^K:.*$/m, `K:${newTune.key?.trim() || ''}`);
    if (updated === prev.abc) return prev;
    return { ...prev, abc: updated };
  });
}, [newTune.key]);

const [originalAbc, setOriginalAbc] = useState('');

  useEffect(() => {
    if (importedTune?.versions) {
      const raw = importedTune.versions[importedTune.selectedIndex].abc || '';
      const cleaned = cleanAndFillAbc({
        rawAbc: raw,
        type: normalizeType(importedTune.type),
        key: normalizeKey(importedTune.key)
      });
      setCustomAbcOverride(cleaned);
      setOriginalAbc(cleaned);

    }
  }, [importedTune?.selectedIndex]);



  const handleChange = (e) => {
const { name, value } = e.target;
// Use functional updater to avoid stale closure overwriting customTags
setNewTune(prev => ({ ...prev, [name]: value }));

// Compute updated values locally for side effects (duplicate checking)
const updated = { ...newTune, [name]: value };
if (name === 'title' && typeof updated.title !== 'string') {
  console.warn('⚠️ Invalid title value:', updated.title);
}

    if ((name === 'title' || name === 'type') && prefs?.enableAutoSuggest !== 'no') {
const lower = (updated?.title ?? '').toString().trim().toLowerCase();

setDuplicateTune(
  tunes.find(t =>
    (t?.title ?? '').toString().trim().toLowerCase() === lower &&
    t?.type === updated?.type
  ) || null
);

setSimilarTunes(
  tunes.filter(t =>
    (t?.title ?? '').toString().trim().toLowerCase().includes(lower) &&
    t?.type === updated?.type &&
    (t?.title ?? '').toString().trim().toLowerCase() !== lower
  )
);

      if (effectiveMode !== 'notes') { // TheSession search in tunes mode only
        if (sessionSearchAbort) sessionSearchAbort.abort();
        if (lower.length < 3) return setSuggestedTunes([]);

        const controller = new AbortController();
        setSessionSearchAbort(controller);
        fetch(`https://thesession.org/tunes/search?q=${encodeURIComponent(updated.title)}&format=json`, { signal: controller.signal })
          .then(res => res.json())
          .then(data => {
            const matches = Array.isArray(data.tunes) ? data.tunes.filter(t => t.name.toLowerCase().includes(lower)).slice(0, 5) : [];
            setSuggestedTunes(matches);
          })
          .catch(err => {
            if (err.name !== 'AbortError') console.error('Suggestion error:', err);
          });
      }
    }
  };

  const addTune = async (skipDuplicateCheck = false) => {
  if (!hasMeaningfulContent(newTune)) {
    setAlertModal({ title: 'Nothing to Save', message: 'Add at least one non-dropdown field before saving.' });
    return;
  }
const resolvedTitle = resolveDraftTitle(newTune);
const normalizedNewTitle = resolvedTitle.toLowerCase();
const normalizedNewType = (newTune?.type ?? '').toString();

const exists = tunes.find(t => 
  (t?.title ?? '').toString().trim().toLowerCase() === normalizedNewTitle &&
  (t?.type ?? '').toString() === normalizedNewType
);
  if (exists && !skipDuplicateCheck) {
    setDuplicateTuneToConfirm(exists);
    return;
  }

  try {
    const willPrompt = !!session;
    const sessionData = []; // Leave blank for now



    // Upload recordings to Firebase Storage if needed
let uploadedRecordings = [];
let recordingUploadFailed = false;
for (const rec of recordings) {
  if (rec.localBlob && navigator.onLine) {
    try {
      const blob = rec.localBlob;
      const ext = (rec.mimeType || blob?.type || '').includes('mp4') ? 'mp4' : 'webm';
      const filename = `recording_${Date.now()}.${ext}`;
      const fullPath = `recordings/${user.uid}/temp_recordings/${filename}`;
      const destRef = ref(storage, fullPath);
      await uploadBytes(destRef, blob);
      const downloadUrl = await getDownloadURL(destRef);
      uploadedRecordings.push({
        url: downloadUrl,
        duration: rec.duration || null,
        label: rec.label || '',
        mimeType: rec.mimeType || blob?.type || null,
        uploadedAt: new Date().toISOString(),
        path: fullPath
      });
    } catch (err) {
      console.error('Recording upload failed:', err?.code, err?.message, err);
      recordingUploadFailed = err?.code || err?.message || 'unknown';
    }
  } else if (rec.url) {
    uploadedRecordings.push({
      url: rec.url,
      duration: rec.duration || null,
      label: rec.label || '',
      uploadedAt: rec.uploadedAt || new Date().toISOString(),
      path: rec.path || '',
    });
  }
}
    const normalizedTune = {
      ...newTune,
      title: resolvedTitle,
      customTags: normalizeTagsArray(newTune.customTags),
      type: normalizeType(newTune.type),
genre: typeof newTune.genre === 'string'
  ? newTune.genre.charAt(0).toUpperCase() + newTune.genre.slice(1).toLowerCase()
  : '',
      key: normalizeKey(newTune.key),
      abc: newTune.abc?.trim() ? `X:1\nT:${resolvedTitle}\n${newTune.abc}` : '',
      chords: '',
      lyrics: newTune.lyrics || '',
      quickNote: newTune.quickNote || '',
      notes: newTune.notes || '',
      link1: newTune.link1 || '',
      link2: '',
      link3: '',
      imageUrl: newTune.imageUrl || '',
      source: '',
      sessions: sessionData,
    recordings: uploadedRecordings,
      attachments: []
    };




    const docId = crypto.randomUUID();

    
    const data = {
      ...normalizedTune,
      mode: effectiveMode,
      eventDate: effectiveMode === 'notes' ? (newTune.eventDate || '') : '',
      createdAt: navigator.onLine ? serverTimestamp() : new Date().toISOString(),
      updatedAt: navigator.onLine ? serverTimestamp() : new Date().toISOString(),
      lastViewedAt: ''
    };


    
    if (navigator.onLine) {
      await setDoc(doc(db, 'users', user.uid, 'tunes', docId), data);
    } else {
await dexie.tunes.put({
  id: docId,
  ...data,
  attachments: attachments.map(file => ({
    name: file.name,
    type: file.type || file.file?.type || 'application/octet-stream',
    uploadedAt: file.uploadedAt || new Date().toISOString(),
    pending: true // 🔁 will need syncing
  }))
});

await addPendingWrite({
  type: 'add',
  collection: 'tunes',
  docId,
  data: {
    ...data,
    attachments: attachments.map(file => ({
      name: file.name,
      type: file.type || file.file?.type || 'application/octet-stream',
      uploadedAt: file.uploadedAt || new Date().toISOString(),
      pending: true
    }))
  }
});

    }

    // Handle attachments only if online (defer otherwise)
    let movedAttachments = [];
    let attachmentUploadFailed = false;
    if (attachments.length > 0 && navigator.onLine) {
      for (const file of attachments) {
        try {
          const fullPath = `recordings/${user.uid}/attachments/${docId}/${file.name}`;
          const destRef = ref(storage, fullPath);
          await uploadBytes(destRef, file.file);
          const newUrl = await getDownloadURL(destRef);

          movedAttachments.push({
            name: file.name,
            type: file.type || file.file?.type || 'application/octet-stream',
            url: newUrl,
            path: fullPath,
            uploadedAt: file.uploadedAt || new Date().toISOString()
          });
        } catch (err) {
          console.error('Attachment upload failed:', file.name, err);
          attachmentUploadFailed = true;
        }
      }

      if (movedAttachments.length > 0) {
        await updateDoc(doc(db, 'users', user.uid, 'tunes', docId), {
          attachments: movedAttachments
        });
        await dexie.tunes.put({ id: docId, ...data, attachments: movedAttachments });
      }
    }

    setNewTune({
      title: '',
      genre: getDefaultForMode(effectiveMode, 'genre'),
      type: getDefaultForMode(effectiveMode, 'type'),
      key: effectiveMode === 'notes' ? '' : (prefs?.defaultKey || ''),
      status: prefs?.defaultStatus || 'Need to Learn',
      composer: '',
      rating: '',
      lyrics: '',
      quickNote: '',
      notes: '',
      link1: '',
      imageUrl: '',
      abc: '',
      eventDate: '',
      recordings: [],
      customTags: []
    });

    setAttachments([]);
    setRecordings([]);
setHasUnsavedRecording(false);
setIsDirty(false);
    setDuplicateTune(null);
    setSimilarTunes([]);
    setSuggestedTunes([]);
    setDismissSuggestedTunes(false);

    // Warn about upload failures
    const uploadWarnings = [];
    if (recordingUploadFailed) uploadWarnings.push(`recording: ${recordingUploadFailed}`);
    if (attachmentUploadFailed) uploadWarnings.push(`file: ${attachmentUploadFailed}`);
    const warningMsg = uploadWarnings.length > 0
      ? ` (Upload failed: ${uploadWarnings.join(', ')})`
      : '';

    refreshCounts();

    if (session) {
      setPendingSessionTuneId(docId);
      setShowSessionPrompt(true);
    } else {
      setConfirmationMessage({
        msg: `${singularItemLabel} added!${warningMsg}`,
        id: docId
      });
    }
    if (warningMsg) console.warn('Upload issues:', warningMsg);


    setTimeout(() => setConfirmationMessage(null), 10000);

try {
  const prefsDocId = 'settings';
  const prefsRef = doc(db, 'users', user.uid, 'preferences', prefsDocId);

  const snap = navigator.onLine ? await getDoc(prefsRef) : null;
  const existing = snap?.exists() && Array.isArray(snap.data().customTags)
    ? snap.data().customTags
    : [];

  const merged = Array.from(new Set([
    ...normalizeTagsArray(newTune.customTags || []),
    ...existing.map(normalizeTag)
  ]));

  if (navigator.onLine) {
    await updateDoc(prefsRef, { customTags: merged });
  } else {
    await dexie.preferences.put({ id: prefsDocId, customTags: merged });
    await addPendingWrite({
      type: 'update',
      collection: 'preferences',
      docId: prefsDocId,
      data: { id: prefsDocId, customTags: merged }
    });
  }
} catch (err) {
  console.warn("Could not sync tags to preferences", err);
}


  } catch (err) {
    console.error(err);
    alert('Error saving tune');
  }
};



const importFromSession = async (tuneId) => {
  try {
    const res = await fetch(`https://thesession.org/tunes/${tuneId}?format=json`);
    const data = await res.json();
    const settings = data.settings || [];

    const versions = settings.map((setting, index) => {
      const abcLines = [
        'X:1',
        `T:${data.name || ''}`,
        `M:${setting.meter || ''}`,
        `L:${setting.unit_note_length || ''}`,
        `R:${data.type || ''}`,
        `Q:${setting.tempo || ''}`,
        `Z:${data.transcribed_by || ''}`,
        `S:${data.source || ''}`,
        `N:${data.note || ''}`,
        `K:${setting.key || ''}`,
        setting.abc || ''
      ];
      return {
        index,
        key: setting.key || '',
        source: setting.source || '',
        transcribed_by: setting.transcribed_by || '',
        abc: abcLines.join('\n'),
      };
    });

    setImportedTune({
      id: tuneId,
      title: data.name || '',
      type: data.type || '',
      key: settings[0]?.key || '',
      versions,
      selectedIndex: 0,
      link: `https://thesession.org/tunes/${tuneId}`
    });

    setImportFields({ title: true, type: true, key: true, abc: true });

  } catch (err) {
    console.error(err);
    alert('Error importing tune');
  }
};


 const saveImportedTune = async () => {
  if (!importedTune) return;

  const rawAbc = customAbcOverride || importedTune.versions?.[importedTune.selectedIndex]?.abc || '';
  const importedAbc = cleanAndFillAbc({
    rawAbc,
    type: importedTune.type,
    key: importedTune.key
  });

  setNewTune(prev => ({
    ...prev,
    ...(importFields.title ? { title: importedTune.title || '' } : {}),
    ...(importFields.type ? { type: normalizeType(importedTune.type || '') } : {}),
    ...(importFields.key ? { key: normalizeKey(importedTune.key || '') } : {}),
    ...(importFields.abc ? { abc: importedAbc } : {}),
    ...(importedTune.link && !(prev.link1 || '').trim() ? { link1: importedTune.link } : {})
  }));

  setImportedTune(null);
  setImportFields({ title: true, type: true, key: true, abc: true });
  setSuggestedTunes([]);
  setDismissSuggestedTunes(false);
  setIsDirty(true);
  setShowMoreFields(true);
  setConfirmationMessage({
    msg: 'Import applied. Continue editing, then click Add Tune.'
  });
  setTimeout(() => setConfirmationMessage(null), 6000);
};

  const customGenresForEffectiveMode = React.useMemo(() => {
    const customGenres = prefs?.customGenres;
    if (Array.isArray(customGenres)) return customGenres;
    return customGenres?.[effectiveMode] || [];
  }, [prefs?.customGenres, effectiveMode]);

  const customTypesForEffectiveMode = React.useMemo(() => {
    const customTypes = prefs?.customTypes;
    if (Array.isArray(customTypes)) return customTypes;
    return customTypes?.[effectiveMode] || [];
  }, [prefs?.customTypes, effectiveMode]);

  const genreOptions = React.useMemo(() => {
    const defaults = effectiveLabels?.defaultGenres || ['Irish', 'Old-Time', 'Bluegrass'];
    const inUse = tunes.filter(t => (t.mode || 'tunes') === effectiveMode).map(t => t.genre).filter(Boolean);
    return getNormalizedGenreOptions([...defaults, ...customGenresForEffectiveMode, ...inUse]);
  }, [customGenresForEffectiveMode, tunes, effectiveLabels?.defaultGenres, effectiveMode]);

  const typeOptions = React.useMemo(() => {
    const defaults = effectiveLabels?.defaultTypes || ['Jig', 'Reel', 'Hornpipe', 'Waltz', 'Polka', 'Song'];
    const inUse = tunes.filter(t => (t.mode || 'tunes') === effectiveMode).map(t => t.type).filter(Boolean);
    const seen = new Map();
    [...defaults, ...customTypesForEffectiveMode, ...inUse].forEach(t => {
      const normalized = t.charAt(0).toUpperCase() + t.slice(1).toLowerCase();
      if (!seen.has(normalized.toLowerCase())) seen.set(normalized.toLowerCase(), normalized);
    });
    return [...seen.values()].sort((a, b) => a.localeCompare(b));
  }, [customTypesForEffectiveMode, tunes, effectiveLabels?.defaultTypes, effectiveMode]);
  const statusOptions = useMemo(
    () => effectiveLabels?.statusOptions || ['Status (—)', 'Need to Learn', 'Learning', 'Learned'],
    [effectiveLabels?.statusOptions]
  );
  const ratingOptions = useMemo(
    () => ['Rating (—)', '★', '★★', '★★★', '★★★★', '★★★★★'],
    []
  );
  const genreSelectOptions = useMemo(
    () => [
      ...genreOptions.map((g) => ({ value: g, label: g })),
      { value: '__add_new__', label: '+ Add new genre...' }
    ],
    [genreOptions]
  );
  const typeSelectOptions = useMemo(
    () => [
      ...typeOptions.map((t) => ({ value: t, label: t })),
      { value: '__add_new__', label: '+ Add new type...' }
    ],
    [typeOptions]
  );
  const statusSelectOptions = useMemo(
    () => statusOptions.map((s) => ({ value: s, label: s })),
    [statusOptions]
  );
  const ratingSelectOptions = useMemo(
    () => ratingOptions.map((r) => ({ value: r, label: r })),
    [ratingOptions]
  );
  const normalizeSearchToolUrl = (value = '') => value.trim().replace(/^https?:\/\//i, '').replace(/\/+$/, '').toLowerCase();
  const configuredSearchTools = useMemo(() => {
    if (Array.isArray(prefs?.searchTools)) {
      return prefs.searchTools
        .map((tool) => ({
          url: normalizeSearchToolUrl(tool?.url || ''),
          enabled: tool?.enabled !== false
        }))
        .filter((tool) => tool.url && tool.enabled);
    }
    const legacyTools = [
      { url: 'folkfriend.app', enabled: prefs?.showFolkfriend !== false },
      { url: 'tunepal.org', enabled: prefs?.showTunepal !== false },
      { url: 'tunearch.org', enabled: prefs?.showTunearch !== false },
      { url: 'bluegrasslyrics.com', enabled: prefs?.showBluegrasslyrics !== false },
      { url: 'thesession.org', enabled: prefs?.showThesession !== false },
      ...((Array.isArray(prefs?.customSearchTools) ? prefs.customSearchTools : []).map((tool) => ({
        url: normalizeSearchToolUrl(typeof tool === 'string' ? tool : (tool?.url || '')),
        enabled: typeof tool === 'string' ? true : tool?.enabled !== false
      })))
    ];
    return legacyTools.filter((tool) => tool.url && tool.enabled);
  }, [prefs]);
  const sessionToolEnabled = effectiveMode !== 'notes' && configuredSearchTools.some((tool) => tool.url === 'thesession.org');
  const userTunesCount = tunes.filter(t => !t.isSample).length;
  const showHints = userTunesCount < 5 || prefs?.showFieldHints;
  const hint = (text, extraStyle = {}) => showHints ? (
    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '2px', fontStyle: 'italic', textAlign: 'center', ...extraStyle }}>{text}</div>
  ) : null;
  const hasMeaningfulContent = (draft) => {
    const notesText = stripHtmlToText(draft.notes || '').trim();
    return Boolean(
      (draft.title || '').trim() ||
      notesText ||
      (draft.lyrics || '').trim() ||
      (draft.quickNote || '').trim() ||
      (draft.link1 || '').trim() ||
      (draft.imageUrl || '').trim() ||
      (draft.abc || '').trim() ||
      (recordings || []).length > 0 ||
      (attachments || []).length > 0
    );
  };
  const resolveDraftTitle = (draft) => {
    const explicit = (draft.title || '').trim();
    if (explicit) return explicit;
    if (effectiveMode === 'notes') {
      const noteLine = firstNonEmptyLine(draft.notes || '');
      if (noteLine) return noteLine;
      return `Untitled — ${formatUntitledDate()}`;
    }
    return `Untitled Tune — ${formatUntitledDate()}`;
  };

useEffect(() => {
  setShowMoreFields(false);
}, [effectiveMode]);

useEffect(() => {
  const handleBeforeUnload = (e) => {
    if (isDirty || hasUnsavedRecording) {
      e.preventDefault();
      e.returnValue = 'You have unsaved changes. Leave anyway?';
    }
  };
  window.addEventListener('beforeunload', handleBeforeUnload);
  return () => window.removeEventListener('beforeunload', handleBeforeUnload);
}, [isDirty, hasUnsavedRecording]);




  if (!user) return <PageWrapperAuto><Login /></PageWrapperAuto>;

  return (
    <PageWrapperAuto>
     <div className="app-title" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
    <img src={myIcon} alt="Logo" className="responsive-icon" />
    <span>{effectiveLabels?.addPageTitle || labels?.addPageTitle || 'Add Tune'}</span>
    <FaQuestionCircle
      onClick={() => navigate(`/help#${effectiveMode === 'notes' ? 'adding-notes' : 'adding-tunes'}`)}
      style={{ fontSize: '0.65em', cursor: 'pointer', color: 'var(--text-link, #228be6)', opacity: 0.8 }}
      title="Help & Tutorials"
    />
  </div>

      <div style={{ height: '10px' }} />

{(
  <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '10px' }}>
    <div style={{
      display: 'inline-flex',
      borderRadius: '12px',
      border: '1px solid var(--card-border, #ccc)',
      overflow: 'hidden',
      backgroundColor: 'var(--input-bg)',
      padding: '2px'
    }}>
      {[{ value: 'tunes', label: 'Add Tune' }, { value: 'notes', label: 'Add Note' }].map((opt) => {
        const activeValue = isFullMode ? addingMode : (currentMode === 'all' ? addingMode : currentMode);
        const isActive = activeValue === opt.value;
        const isLocked = !isFullMode && currentMode !== 'all' && !isActive;
        return (
          <button
            key={opt.value}
            onClick={async () => {
              if (isLocked) { setPendingLockedMode(opt.value); return; }
              setAddingMode(opt.value);
              if (isFullMode) {
                await userPrefsContext.patchPrefs({ appMode: opt.value });
                document.body.setAttribute('data-app-mode', opt.value);
              }
            }}
            style={{
              padding: '5px 12px',
              fontSize: '0.78rem',
              fontWeight: isActive ? 700 : 500,
              backgroundColor: isActive ? 'var(--bg-card)' : 'transparent',
              color: 'var(--text-primary)',
              border: 'none',
              cursor: isLocked ? 'default' : 'pointer',
              opacity: isLocked ? 0.45 : 1,
              transition: 'background-color 0.15s, color 0.15s',
              borderRadius: '9px',
              boxShadow: isActive ? '0 1px 3px rgba(0,0,0,0.14)' : 'none'
            }}
          >
            {opt.label}{isLocked ? ' 🔒' : ''}
          </button>
        );
      })}
    </div>
  </div>
)}

{showHints && (
  <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', fontStyle: 'italic', textAlign: 'center', margin: '0.5rem auto', maxWidth: '400px', lineHeight: 1.5 }}>
    {effectiveMode === 'notes'
      ? 'Your personal note-taking app — capture ideas, links, references, and tasks with rich text, tags, ratings, and flexible organization.'
      : currentMode === 'all'
        ? 'Add tunes or notes to your library. Use the toggle above to switch what you\'re adding.'
        : 'Your personal music database — store tunes, track learning, record audio, read ABC notation, organize setlists, and share with fellow musicians.'}
    <div style={{ fontSize: '0.7rem', marginTop: '0.25rem', opacity: 0.7 }}>
      <span onClick={() => navigate('/help')} style={{ cursor: 'pointer', textDecoration: 'underline' }}>Toggle hints in Help &amp; Tutorials</span>
    </div>
  </div>
)}


      <div
        className={`add-tune-form ${effectiveMode === 'notes' ? 'add-notes-layout' : ''}`}
        style={{ display: 'flex', flexDirection: 'column', gap: '10px', margin: '0 auto 20px auto', alignItems: 'center', width: '100%', maxWidth: '500px' }}
      >


 <div style={{ width: '100%', maxWidth: '300px', margin: '0 auto' }}>
<input
  className="search-input"
  name="title"
  placeholder="Title"
  value={typeof newTune.title === 'string' ? newTune.title : ''}
  onChange={handleChange}
  style={{ width: '100%', boxSizing: 'border-box', gridColumn: 'auto' }}
/>
{hint(effectiveMode === 'notes' ? 'Title of your note' : 'The name of the tune, song, or piece')}

</div>

{addFields?.notes !== false && effectiveMode === 'notes' && (
  <div style={{ width: '100%', maxWidth: '300px', margin: '0 auto' }}>
    <NotesEditor
      value={newTune.notes}
      onChange={(val) => setNewTune(prev => ({ ...prev, notes: val }))}
      placeholder="Write your note..."
      onFullscreen={() => setFullscreenNotes(true)}
      enableInternalLinks={effectiveMode === 'notes'}
      internalLinkTargets={internalLinkTargets}
      onInternalLinkNavigate={handleNavigateInternalLink}
    />
  </div>
)}

<div style={{ width: '100%', maxWidth: '300px', margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 'var(--ui-filter-grid-gap, 8px)' }}>
  <div style={{ minWidth: 0 }}>
    <AppSelect
      className="search-core-select"
      value={newTune.genre}
      onChange={(value) => {
        if (value === '__add_new__') {
          const input = window.prompt('Enter new genre name:');
          if (input?.trim()) {
            const normalized = input.trim().charAt(0).toUpperCase() + input.trim().slice(1).toLowerCase();
            setNewTune(prev => ({ ...prev, genre: normalized }));
            const prefsRef = doc(db, 'users', user.uid, 'preferences', 'settings');
            getDoc(prefsRef).then(snap => {
              const existing = snap.exists() && Array.isArray(snap.data().customGenres) ? snap.data().customGenres : [];
              if (!existing.some(g => g.toLowerCase() === normalized.toLowerCase())) {
                updateDoc(prefsRef, { customGenres: [...existing, normalized] });
              }
            });
          }
          return;
        }
        handleChange({ target: { name: 'genre', value } });
      }}
      options={genreSelectOptions}
      placeholder="Genre"
      active={Boolean(newTune.genre)}
    />
    {hint(effectiveMode === 'notes' ? 'Organize by category' : 'Musical tradition — Irish, Old-Time, Bluegrass, or add your own')}
  </div>
  <div style={{ minWidth: 0 }}>
    <AppSelect
      className="search-core-select"
      value={newTune.type}
      onChange={(value) => {
        if (value === '__add_new__') {
          const input = window.prompt('Enter new type name:');
          if (input?.trim()) {
            const normalized = input.trim().charAt(0).toUpperCase() + input.trim().slice(1).toLowerCase();
            setNewTune(prev => ({ ...prev, type: normalized }));
            const prefsRef = doc(db, 'users', user.uid, 'preferences', 'settings');
            getDoc(prefsRef).then(snap => {
              const existing = snap.exists() && Array.isArray(snap.data().customTypes) ? snap.data().customTypes : [];
              if (!existing.some(t => t.toLowerCase() === normalized.toLowerCase())) {
                updateDoc(prefsRef, { customTypes: [...existing, normalized] });
              }
            });
          }
          return;
        }
        handleChange({ target: { name: 'type', value } });
      }}
      options={typeSelectOptions}
      placeholder="Type"
      active={Boolean(newTune.type)}
    />
    {hint(effectiveMode === 'notes' ? 'Note, Link, File, Idea, Task, etc.' : 'Jig, Reel, Hornpipe, Song, etc.')}
  </div>
</div>

  


{(effectiveMode !== 'notes') && (showKeyField || showComposerField) && (
<div style={{ width: '100%', maxWidth: '300px', margin: '0 auto', display: 'grid', gridTemplateColumns: (showKeyField && showComposerField) ? 'repeat(2, minmax(0, 1fr))' : '1fr', gap: 'var(--ui-filter-grid-gap, 8px)' }}>
  {showKeyField && (
  <div style={{ minWidth: 0 }}>
    <KeyInput
      value={newTune.key}
      onChange={handleChange}
      style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }}
      suggestions={rawSuggestions}
      inputStyle={{ ...inputStyle }}
      textStyle={textStyle}
    />
    {hint('Musical key — e.g. D major, G dorian', { textAlign: 'left', paddingLeft: 'var(--ui-select-pad-x, 10px)' })}
  </div>
  )}
  {showComposerField && (<>
  <div style={{ minWidth: 0 }}>
  <input
    className="search-input"
    name="composer"
    placeholder="Composer"
    value={newTune.composer}
    onChange={handleChange}
    style={{ width: '100%', boxSizing: 'border-box', gridColumn: 'auto' }}
  />
  {hint('Who wrote or arranged this piece')}
  </div>
  </>)}
</div>
)}

{effectiveMode !== 'notes' && (
<div style={{ width: '100%', maxWidth: '300px', margin: '0 auto' }}>
  <input
    className="search-input"
    name="quickNote"
    placeholder="Quick note (memory jog, shows in preview)"
    value={newTune.quickNote}
    onChange={handleChange}
    style={{ width: '100%', boxSizing: 'border-box', gridColumn: 'auto' }}
  />
  {hint('A short memory jog that shows when browsing sets')}
</div>
)}

{importedTune && (
<div style={{
  width: '100%',
  maxWidth: '280px',
  margin: '0 auto',
  padding: '8px 10px',
  borderRadius: '10px',
  border: '1px solid color-mix(in srgb, var(--card-border) 72%, transparent)',
  background: 'color-mix(in srgb, var(--bg-card) 86%, var(--button-bg) 14%)'
}}>
  <div style={{ textAlign: 'center', fontSize: 'var(--ui-compact-font)', fontWeight: 700, marginBottom: '6px' }}>
    Review Import
  </div>
  <div style={{ display: 'flex', gap: '6px', justifyContent: 'center', marginBottom: '8px', flexWrap: 'wrap' }}>
    <button className="btn-subtle search-control-btn" onClick={saveImportedTune}>
      Apply Import
    </button>
    <button
      className="btn-subtle search-control-btn"
      onClick={() => {
        setImportedTune(null);
        setImportFields({ title: true, type: true, key: true, abc: true });
        setCustomAbcOverride('');
      }}
    >
      Cancel
    </button>
  </div>

  {Object.keys(importFields)
    .filter(f => f !== 'abc')
    .map((field) => (
      <label
        key={field}
        className="text-scaled"
        style={{ display: 'block', marginBottom: '6px', fontSize: 'var(--ui-subtle-font)' }}
      >
        <input
          type="checkbox"
          checked={importFields[field]}
          onChange={(e) =>
            setImportFields(prev => ({
              ...prev,
              [field]: e.target.checked
            }))
          }
        />
        <strong> {field.charAt(0).toUpperCase() + field.slice(1)}:</strong>{' '}
        <span style={{ color: 'var(--text-muted)' }}>
          {field === 'key'
            ? normalizeKey(importedTune.key || '')
            : field === 'type'
              ? normalizeType(importedTune.type || '')
              : typeof importedTune[field] === 'string'
                ? importedTune[field] || '(none)'
                : '(unavailable)'}
        </span>
      </label>
    ))}

  <label className="text-scaled" style={{ display: 'block', marginBottom: '6px', fontSize: 'var(--ui-subtle-font)' }}>
    <input
      type="checkbox"
      checked={importFields.abc}
      onChange={(e) =>
        setImportFields(prev => ({ ...prev, abc: e.target.checked }))
      }
    />
    <strong> ABC:</strong>{' '}
    <span style={{ color: 'var(--text-muted)' }}>
      {customAbcOverride?.trim() ? 'Preview and save' : '(none)'}
    </span>
  </label>

  <AbcImportPreview
    importedTune={importedTune}
    setImportedTune={setImportedTune}
    importFields={importFields}
    customAbcOverride={customAbcOverride}
    setCustomAbcOverride={setCustomAbcOverride}
    setOriginalAbc={setOriginalAbc}
  />
</div>
)}

{suggestedTunes.length > 0 && !dismissSuggestedTunes && !importedTune && (
  <div className="text-scaled" style={{
    width: '100%',
    maxWidth: '260px',
    margin: '0 auto',
    padding: '6px 8px',
    borderRadius: '10px',
    border: '1px solid color-mix(in srgb, var(--card-border) 72%, transparent)',
    background: 'color-mix(in srgb, var(--bg-card) 84%, var(--button-bg) 16%)'
  }}>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', flexWrap: 'wrap' }}>
      <span style={{
        fontSize: 'var(--ui-subtle-font)',
        color: 'var(--text-muted)',
        fontStyle: 'normal'
      }}>
        Suggestions from thesession.org
      </span>
      <button
        type="button"
        className="btn-subtle"
        onClick={() => setDismissSuggestedTunes(true)}
        style={{
          minHeight: 'auto',
          fontSize: '0.9rem',
          lineHeight: 1,
          padding: '0 6px',
          borderRadius: '999px',
          border: '1px solid color-mix(in srgb, var(--card-border) 70%, transparent)',
          background: 'color-mix(in srgb, var(--button-bg) 70%, transparent)'
        }}
        aria-label="Dismiss suggestions"
      >
        ×
      </button>
    </div>
    <ul style={{ listStyle: 'none', paddingLeft: 0, margin: '4px 0 0 0', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
      {suggestedTunes.map((t, i) => (
      <li key={i}>
        <button className="btn-subtle search-control-btn suggested-tune-btn" onClick={() => importFromSession(t.id)}>{t.name} ({t.type})</button>
      </li>
    ))}
    </ul>
  </div>
)}

{(isTuneOrAllMode || effectiveMode === 'notes') && (
<div style={{ width: '100%', maxWidth: '300px', margin: '0 auto' }}>
  <button
    type="button"
    className={`btn-subtle search-control-btn search-more-btn${showMoreFields ? ' panel-open' : ''}`}
    style={{ width: '100%' }}
    onClick={() => setShowMoreFields(prev => !prev)}
  >
    {showMoreFields ? 'Fewer Fields' : 'More Fields'}
  </button>
</div>
)}

{showExpandedFields && (
<>
{(effectiveMode === 'notes') && (showKeyField || showComposerField) && (
<div style={{ width: '100%', maxWidth: '300px', margin: '0 auto', display: 'grid', gridTemplateColumns: (showKeyField && showComposerField) ? 'repeat(2, minmax(0, 1fr))' : '1fr', gap: 'var(--ui-filter-grid-gap, 8px)' }}>
  {showKeyField && (
  <div style={{ minWidth: 0 }}>
  <KeyInput
    value={newTune.key}
    onChange={handleChange}
    style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }}
    suggestions={rawSuggestions}
    inputStyle={{ ...inputStyle }}
    textStyle={textStyle}
  />
  {hint('Musical key — e.g. D major, G dorian', { textAlign: 'left', paddingLeft: 'var(--ui-select-pad-x, 10px)' })}
  </div>
  )}
  {showComposerField && (
  <div style={{ minWidth: 0 }}>
  <input
    className="search-input"
    name="composer"
    placeholder="Composer"
    value={newTune.composer}
    onChange={handleChange}
    style={{ width: '100%', boxSizing: 'border-box', gridColumn: 'auto' }}
  />
  {hint('Who wrote or arranged this piece')}
  </div>
  )}
</div>
)}
{(addFields?.status !== false || addFields?.rating !== false) && (
<div style={{ width: '100%', maxWidth: '300px', margin: '0 auto', display: 'grid', gridTemplateColumns: (addFields?.status !== false && addFields?.rating !== false) ? 'repeat(2, minmax(0, 1fr))' : '1fr', gap: 'var(--ui-filter-grid-gap, 8px)' }}>
  {addFields?.status !== false && (
  <div style={{ minWidth: 0 }}>
    <AppSelect
      className="search-advanced-select"
      value={newTune.status}
      onChange={(value) => handleChange({ target: { name: 'status', value } })}
      options={statusSelectOptions}
      placeholder="Status (—)"
      active={Boolean(newTune.status && newTune.status !== 'Status (—)')}
    />
    {hint(effectiveLabels.statusHint)}
  </div>
  )}
  {addFields?.rating !== false && (
  <div style={{ minWidth: 0 }}>
    <AppSelect
      className="search-advanced-select"
      value={newTune.rating}
      onChange={(value) => handleChange({ target: { name: 'rating', value } })}
      options={ratingSelectOptions}
      placeholder="Rating (—)"
      active={Boolean(newTune.rating && newTune.rating !== 'Rating (—)')}
    />
    {hint('Rate from 1–5 stars to track favorites')}
  </div>
  )}
</div>
)}



<div style={{
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--ui-filter-grid-gap, 8px)',
  width: '100%',
  maxWidth: '300px',
  margin: '0 auto 0px'
}}>
  
  {addFields?.notes !== false && effectiveMode !== 'notes' && (
      <textarea
        className="search-input add-entry-textarea add-notes-field"
        name="notes"
        placeholder="Notes"
        value={newTune.notes}
        onChange={handleChange}
        rows={2}
        onInput={e => { e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px'; }}
        style={{ width: '100%', boxSizing: 'border-box', resize: 'none', overflow: 'hidden', minHeight: '2.5rem', gridColumn: 'auto' }}
      />
  )}

  {addFields?.lyrics !== false && !(effectiveLabels?.autoHideFields || []).includes('lyrics') && (<>
  <textarea
    className="search-input add-entry-textarea add-lyrics-field"
    name="lyrics"
    placeholder="Lyrics"
    value={newTune.lyrics}
    onChange={handleChange}
    rows={2}
    onInput={e => { e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px'; }}
    style={{ width: '100%', boxSizing: 'border-box', resize: 'none', overflow: 'hidden', minHeight: '2.5rem', gridColumn: 'auto' }}
  />
  {hint('Add lyrics and chord symbols \u2014 tap to edit fullscreen in Courier font')}
  </>)}

  {addFields?.abc !== false && effectiveMode !== 'notes' && (
  <>
    <textarea
      className="search-input add-entry-textarea add-abc-field"
      name="abc"
      placeholder={"M:4/4\nL:1/8\nK:Cmaj\n| notes here |"}
      value={newTune.abc}
      onChange={handleChange}
      onFocus={(e) => {
        if (!newTune.abc) {
          const k = newTune.key?.trim() || '';
          const template = `M:4/4\nL:1/8\nK:${k || ''}\n| `;
          setNewTune(prev => ({ ...prev, abc: template }));
          if (effectiveMode === 'tunes') {
            const abcInput = e.currentTarget;
            requestAnimationFrame(() => {
              const firstBarIndex = template.indexOf('|');
              if (firstBarIndex === -1) return;
              const caretIndex = firstBarIndex + 1;
              abcInput.setSelectionRange(caretIndex, caretIndex);
            });
          }
        }
      }}
      rows={4}
      onInput={e => { e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px'; }}
      style={{ width: '100%', boxSizing: 'border-box', fontSize: '0.85rem', resize: 'none', overflow: 'hidden', minHeight: '2.5rem', gridColumn: 'auto' }}
    />
    {hint('Sheet music in text format — import from TheSession.org or type your own')}
    {newTune.abc?.trim() && (() => {
      const abcForPreview = newTune.abc
        .replace(/^K:.*$/m, `K:${newTune.key?.trim() || 'C'}`);
      const hasNotes = abcForPreview.split('\n').some(l => !/^[A-Z]:/.test(l.trim()) && l.trim());
      return <AbcNotation compact abc={`X:1\nT:${newTune.title || ''}\n${abcForPreview}${hasNotes ? '' : '\nz4|'}`} />;
    })()}
  </>
  )}

     {/* Tags field row */}
  {addFields?.tags !== false && (<>
  <CustomTagInput
    tags={newTune.customTags}
    setTags={(tags) => setNewTune(prev => ({ ...prev, customTags: tags }))}
    suggestions={tagSuggestions}
    focusOnOpen={false}
    inputClassName="search-input"
  />
  {hint('Flexible labels for cross-cutting organization \u2014 filter by AND or OR logic', { textAlign: 'left', paddingLeft: 'var(--ui-select-pad-x, 10px)' })}
  </>)}
</div>

{/* Attachments */}
{addFields?.attachments !== false && (<>
<div className="file-upload-row">
  <input
    className="search-input add-file-input"
    ref={fileInputRef}
    type="file"
    accept={ATTACHMENT_ACCEPT_ATTR}
    multiple
    onChange={handleFileUpload}
    style={{ width: '100%', boxSizing: 'border-box' }}
  />
</div>
{hint(effectiveMode === 'notes' ? 'Attach images or PDFs' : 'Attach images or PDFs \u2014 sheet music, chord charts, album art')}
</>)}

{/* Web Link & Image URL */}
<div style={{ width: '100%', maxWidth: '300px', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 'var(--ui-filter-grid-gap, 8px)' }}>
  {addFields?.link1 !== false && (<>
  <input
    className="search-input"
    name="link1"
    type="url"
    placeholder="Web link (URL)"
    value={newTune.link1}
    onChange={handleChange}
    style={{ width: '100%', boxSizing: 'border-box', gridColumn: 'auto' }}
  />
  {hint('A reference URL, YouTube video, or resource')}
  </>)}

  {addFields?.imageUrl !== false && (
  <input
    className="search-input"
    name="imageUrl"
    type="url"
    placeholder="Image URL (e.g. permalink to a .png or .jpg)"
    value={newTune.imageUrl}
    onChange={handleChange}
    style={{ width: '100%', boxSizing: 'border-box', gridColumn: 'auto' }}
  />
  )}
</div>
 </>
)}

{/* Audio Recorder */}
{addFields?.recordings !== false && (effectiveMode !== 'notes' || showExpandedFields) && (
<div style={{ width: '100%', maxWidth: '300px', marginTop: '0rem' }}>
<div style={{ width: '100%', maxWidth: '300px', margin: '0 auto' }}>
  <TuneRecorder
    key={confirmationMessage?.id || 'new'}
    tune={{ title: newTune.title || 'Untitled' }}
    recordings={recordings}
    onUpdateRecordings={(r) => {
      setRecordings(r);
      setHasUnsavedRecording(r.length > 0);
    }}
    containerStyle={{
      marginTop: '0'
    }}
  />
  {hint(effectiveMode === 'notes' ? 'Record audio \u2014 voice memos, dictation, or reference clips' : 'Record audio directly \u2014 great for remembering how a tune goes')}
</div>




  {/* Audio preview of local recordings */}
{recordings.length > 0 && (
  <div style={{ marginTop: '1rem' }}>
    {recordings.map((rec, i) => (
      <div key={rec.id || i} style={{ marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '8px' }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: '0.85rem', marginBottom: '4px', color: '#555' }}>
            {rec.label || `Recording ${i + 1}`}
          </div>
          <AudioPlayerWithSpeed src={getStableRecordingAudioUrl(rec)} compact />
        </div>
        <button
          onClick={() => setRecordingToDelete(rec)}
          style={{
            background: 'transparent',
            border: 'none',
            fontSize: '1.2rem',
            color: '#c00',
            cursor: 'pointer',
            lineHeight: 1,
            padding: 0,
          }}
          title="Delete recording"
          aria-label={`Delete recording ${rec.label || i + 1}`}
        >
          🗑
        </button>
      </div>
    ))}
  </div>
)}

  </div>
)}



{addFields?.attachments !== false && (
<div style={{ marginTop: '0.5rem', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
  {attachments.map((file, i) => (
    <div
      key={i}
      style={{
        fontSize: '0.85rem',
        color: 'var(--text-secondary)',
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        marginBottom: '4px',
        maxWidth: '280px',
        justifyContent: 'space-between',
        width: '100%',
      }}
    >
      <span style={{ overflowWrap: 'anywhere', flex: 1 }}>{file.name}</span>
      <button
        className="btn-subtle"
        onClick={() => {
          setAttachments(prev => prev.filter((_, index) => index !== i));
        }}
        style={{
          background: 'transparent',
          border: 'none',
          fontSize: '1rem',
          color: 'var(--text-secondary)',
          cursor: 'pointer',
          lineHeight: 1,
          flexShrink: 0
        }}
        title="Remove file"
      >
        ×
      </button>
    </div>
  ))}
</div>
)}

<button className="btn-subtle btn-commit search-control-btn" onClick={addTune}>{effectiveLabels?.addButton || 'Add Tune'}</button>

    {confirmationMessage && (
      <div
        className={`text-scaled add-success-banner ${confirmationMessage?.id ? 'add-success-banner-clickable' : ''}`}
        onClick={() => {
          if (confirmationMessage?.id) navigate(`/tune/${confirmationMessage.id}`);
        }}
      >
        {confirmationMessage.msg}
        {confirmationMessage?.id && <span style={{ opacity: 0.78 }}> (Tap to view {singularItemLabelLower})</span>}
      </div>
    )}

    {NudgeBanner && <div style={{ width: '100%', maxWidth: '300px', margin: '8px auto 0' }}><NudgeBanner /></div>}

{(!effectiveLabels?.hideSearchTools || effectiveMode === 'notes') && (
<>
<div style={{ width: '100%', maxWidth: '300px', marginTop: '12px' }}>
  <button
    type="button"
    className={`btn-subtle search-control-btn search-more-btn${showSearchTools ? ' panel-open' : ''}`}
    style={{ width: '100%' }}
    onClick={() => setShowSearchTools(prev => !prev)}
  >
    {showSearchTools
      ? (effectiveMode === 'notes' ? 'Hide Import Tools' : 'Hide Import & Search Tools')
      : (effectiveMode === 'notes' ? 'Import Tools' : 'Import & Search Tools')}
  </button>
</div>
{showSearchTools && (
<>
  {effectiveMode !== 'notes' && (
    <>
	    {hint('Look up this tune on external sites. Add your own custom search tools in Settings.')}
	    <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', flexWrap: 'wrap', maxWidth: '500px' }}>
	      {configuredSearchTools
	        .filter((tool) => effectiveMode !== 'notes' || tool.url !== 'thesession.org')
	        .map((tool, i) => (
	          <div key={`tool-${tool.url}-${i}`} style={{ width: '160px' }}>
	            <button
	              className="btn-subtle search-control-btn"
	              onClick={() => {
	                if (tool.url === 'tunepal.org') return openTunepalSearch();
	                if (tool.url === 'thesession.org') return openSessionSearchPage();
	                window.open(tool.url.startsWith('http') ? tool.url : `https://${tool.url}`, '_blank');
	              }}
	              style={{ width: '100%' }}
	            >
	              {tool.url}
	            </button>
	          </div>
	        ))}
	    </div>
	    {sessionToolEnabled && (
	    <div style={{ marginTop: '6px', width: '100%', maxWidth: '300px', textAlign: 'center' }}>
	  <div style={{ display: 'flex', gap: '6px', justifyContent: 'center', flexWrap: 'wrap' }}>
	    <input
	      className="search-input"
	      type="text"
	      value={pastedSessionId}
	      onChange={(e) => setPastedSessionId(e.target.value)}
	      placeholder="Paste thesession.org url or ID"
	      style={{
	        gridColumn: 'auto',
	        flex: 1,
	        minWidth: '160px',
	        maxWidth: '250px',
	        textAlign: 'center'
	      }}
	    />
	    <button
	      className="btn-subtle search-control-btn"
	      onClick={handleImportFromPastedSessionId}
	      style={{ minHeight: 'var(--ui-select-height, 28px)' }}
	    >
	      Import
	    </button>
	  </div>
	  {hint('tap share → copy from thesession')}
	</div>
	)}
{effectiveMode !== 'notes' && (
  <div style={{ width: '100%', maxWidth: '360px', margin: '8px auto 0' }}>
    {hint('Import an entire public tunebook from TheSession.org')}
    <button
      type="button"
      className="btn-subtle search-control-btn"
      style={{ width: '100%' }}
      onClick={() => setShowTSImportModal(true)}
    >
      Bulk import from thesession.org
    </button>
    {showTSImportModal && (
      <TheSessionImportModal onClose={() => setShowTSImportModal(false)} />
    )}
  </div>
)}
    </>
  )}
<div style={{ width: '100%', maxWidth: '360px', margin: '0 auto', opacity: isSimpleMode ? 0.4 : 1, pointerEvents: isSimpleMode ? 'none' : 'auto' }}>
  <BulkTuneImporter
    user={user}
    effectiveMode={effectiveMode}
    tunes={tunes}
    genreOptions={genreOptions}
    typeOptions={typeOptions}
    defaultGenre={getDefaultForMode(effectiveMode, 'genre')}
    defaultType={getDefaultForMode(effectiveMode, 'type')}
    defaultStatus={prefs?.defaultStatus || ''}
    onComplete={(msg) => {
      setConfirmationMessage({ msg });
      setTimeout(() => setConfirmationMessage(null), 8000);
    }}
  />
</div>
</>
)}
</>
)}









      </div>



{showSessionPrompt && (
  <SessionPromptModal
    message={`Add this ${effectiveLabels?.item || 'item'} to the active session?`}
    onConfirm={async () => {
      try {
        const ref = doc(db, 'users', user.uid, 'tunes', pendingSessionTuneId);
        const snap = await getDoc(ref);
        const tune = snap.exists() ? snap.data() : null;
        if (tune) {
          const updatedSessions = [...(tune.sessions || []), {
            sessionId: session.id,
            location: session.location,
            startedAt: session.startedAt || new Date().toISOString()
          }];
          await updateDoc(ref, { sessions: updatedSessions });
          await dexie.tunes.put({ id: pendingSessionTuneId, ...tune, sessions: updatedSessions });
        }
      } catch (err) {
        console.error('Error updating tune with session:', err);
      }

      setConfirmationMessage({
        msg: `${singularItemLabel} tagged to active session`,
        id: pendingSessionTuneId
      });
      setPendingSessionTuneId(null);
      setShowSessionPrompt(false);
    }}
    onCancel={() => {
      setConfirmationMessage({
        msg: `${singularItemLabel} saved without session tag`,
        id: pendingSessionTuneId
      });
      setPendingSessionTuneId(null);
      setShowSessionPrompt(false);
    }}
  />
)}

<ActionConfirmModal
  open={Boolean(alertModal)}
  title={alertModal?.title || ''}
  message={alertModal?.message || ''}
  confirmLabel="OK"
  hideCancel
  onConfirm={() => setAlertModal(null)}
  onCancel={() => setAlertModal(null)}
/>

<ActionConfirmModal
  open={confirmDiscardRecordingOpen}
  title="Discard Recording"
  message="Discard the current recording? This cannot be undone."
  confirmLabel="Discard"
  cancelLabel="Keep Recording"
  danger
  onConfirm={() => {
    setConfirmDiscardRecordingOpen(false);
    cancelRecording();
  }}
  onCancel={() => setConfirmDiscardRecordingOpen(false)}
/>

<ActionConfirmModal
  open={Boolean(duplicateTuneToConfirm)}
  title="Possible Duplicate"
  message={duplicateTuneToConfirm ? `A tune titled "${duplicateTuneToConfirm.title}" already exists as ${duplicateTuneToConfirm.type}. Add anyway?` : ''}
  confirmLabel="Add Anyway"
  cancelLabel="Cancel"
  onConfirm={() => {
    setDuplicateTuneToConfirm(null);
    addTune(true);
  }}
  onCancel={() => setDuplicateTuneToConfirm(null)}
/>

<ActionConfirmModal
  open={Boolean(recordingToDelete)}
  title="Delete Recording"
  message="Delete this recording? This cannot be undone."
  confirmLabel="Delete"
  cancelLabel="Cancel"
  danger
  onConfirm={() => {
    if (!recordingToDelete) return;
    const updated = recordings.filter(r => r.id !== recordingToDelete.id);
    setRecordings(updated);
    setHasUnsavedRecording(updated.length > 0);
    setRecordingToDelete(null);
  }}
  onCancel={() => setRecordingToDelete(null)}
/>

<ActionConfirmModal
  open={Boolean(pendingLockedMode)}
  title={pendingLockedMode === 'notes' ? 'Enable Notes Mode?' : 'Enable Tunes Mode?'}
  message={pendingLockedMode === 'notes'
    ? 'Notes mode lets you capture ideas, links, tasks, and references with rich text. Add Notes to your app?'
    : 'Tunes mode is for tracking your musical repertoire — add tunes, make sets, and log sessions. Switch to Tunes + Notes?'}
  confirmLabel="Yes, enable it"
  cancelLabel="Not now"
  onConfirm={async () => {
    if (!pendingLockedMode || !prefs) return;
    const nextMode = currentMode === 'all' ? currentMode : 'all';
    await userPrefsContext.patchPrefs({ appMode: nextMode });
    document.body.setAttribute('data-app-mode', nextMode);
    setAddingMode(pendingLockedMode);
    setPendingLockedMode(null);
  }}
  onCancel={() => setPendingLockedMode(null)}
/>

<FullscreenNotesEditorOverlay
  open={fullscreenNotes}
  titleValue={newTune.title}
  onTitleChange={(value) => setNewTune((prev) => ({ ...prev, title: value }))}
  notesValue={newTune.notes}
  onNotesChange={(val) => setNewTune((prev) => ({ ...prev, notes: val }))}
  placeholder="Write your note..."
  onClose={() => setFullscreenNotes(false)}
  inputStyle={inputStyle}
  enableInternalLinks={effectiveMode === 'notes'}
  internalLinkTargets={internalLinkTargets}
  onInternalLinkNavigate={handleNavigateInternalLink}
/>

    </PageWrapperAuto>
  );
}
